import { useState, useEffect } from "react";
import { sellerService } from "../../services/enhancedCrudService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

const SellerCatalogs = () => {
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [display_name, setDisplayName] = useState("");
  const [storageInfo, setStorageInfo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadId, setUploadId] = useState(null);

  const fetchCatalogs = async () => {
    try {
      setLoading(true);
      const response = await sellerService.getCatalogs();
      setCatalogs(response || []);
    } catch (error) {
      console.error("Error fetching catalogs:", error);
      toast.error("Failed to load catalogs");
    } finally {
      setLoading(false);
    }
  };

  const fetchStorageInfo = async () => {
    try {
      const response = await sellerService.getCatalogsUsedStorage();
      setStorageInfo(response || null);
    } catch (error) {
      console.error("Error fetching storage info:", error);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error("Please select a file to upload");
      return;
    }
    if (!display_name) {
      toast.error("Please enter a display name");
      return;
    }

    const CHUNK_SIZE = 1024 * 5120; // 1MB chunks
    const fileSize = selectedFile.size;
    const totalChunks = Math.ceil(fileSize / CHUNK_SIZE);

    // Generate unique upload ID
    const currentUploadId = uploadId || `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setUploadId(currentUploadId);

    try {
      setUploading(true);
      setUploadProgress(0);

      // Upload chunks sequentially
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, fileSize);
        const chunk = selectedFile.slice(start, end);

        const formData = new FormData();
        formData.append("chunkIndex", chunkIndex);
        formData.append("totalChunks", totalChunks);
        formData.append("uploadId", currentUploadId);
        formData.append("display_name", display_name);
        formData.append("file", chunk);
        try {
          await sellerService.uploadCatalogChunk(formData);
          const progress = ((chunkIndex + 1) / totalChunks) * 100;
          setUploadProgress(progress);
        } catch (chunkError) {
          console.error(`Error uploading chunk ${chunkIndex}:`, chunkError);
          throw new Error(`Failed to upload chunk ${chunkIndex + 1} of ${totalChunks}`);
        }
      }

      toast.success("Catalog uploaded successfully!");
      setSelectedFile(null);
      setDisplayName("");
      setUploadProgress(0);
      setUploadId(null);
      fetchCatalogs();
      fetchStorageInfo();
    } catch (error) {
      console.error("Error uploading catalog:", error);
      toast.error(error.message || "Failed to upload catalog");
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (catalogId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you really want to delete this catalog?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await sellerService.deleteCatalog(catalogId);
        toast.success("Catalog deleted successfully");
        fetchCatalogs();
        fetchStorageInfo();
      } catch (error) {
        console.error("Error deleting catalog:", error);
        toast.error("Failed to delete catalog");
      }
    }
  };

  useEffect(() => {
    fetchCatalogs();
    fetchStorageInfo();
  }, []);

  if (loading) {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-body">
              <div className="text-center">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="card">
          <div className="card-header">
            <h4 className="card-title">Seller Catalogs</h4>
          </div>
          <div className="card-body">
            <div className="row">
              <div className="col-md-8">
                <div className="table-responsive">
                  <table className="table table-bordered table-hover align-middle">
                    <thead className="thead-dark">
                      <tr>
                        <th style={{ width: "60px" }}>Sr.No</th>
                        <th>Catalog Name</th>
                        <th style={{ width: "120px" }}>File Size</th>
                        <th style={{ width: "150px" }}>Upload Date</th>
                        <th style={{ width: "80px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {catalogs.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center text-muted">
                            No catalogs uploaded yet
                          </td>
                        </tr>
                      ) : (
                        catalogs.map((catalog, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <a
                                href={catalog.file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary fw-semibold"
                              >
                                {catalog.display_name || "Catalog File"}
                              </a>
                            </td>
                            <td>
                              {catalog.size ? `${catalog.size} KB` : "N/A"}
                            </td>
                            <td>
                              {catalog.uploaded_at
                                ? new Date(
                                    catalog.uploaded_at
                                  ).toLocaleDateString()
                                : "N/A"}
                            </td>
                            <td className="text-center">
                              <i
                                className="fas fa-trash text-danger"
                                onClick={() => handleDelete(catalog.id)}
                              ></i>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card mb-4">
                  <div className="card-header">
                    <h5 className="text-dark mb-0" style={{ color: "#6f42c1" }}>
                      Upload New Catalog
                    </h5>
                    {storageInfo && (
                      <div className="row mb-3">
                        <div className="col-md-12">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Storage Used:</strong>{" "}
                              <span className="text-primary">
                                {storageInfo.catalogUsedStorageMB
                                  ? `${storageInfo.catalogUsedStorageMB.toFixed(
                                      6
                                    )} MB`
                                  : "0.000000 MB"}
                              </span>
                            </div>{" "}
                            <div>
                              <strong>Limit:</strong>
                              <span className="text-success">1024 MB</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {storageInfo &&
                      storageInfo.catalogUsedStorageMB &&
                      (storageInfo.catalogUsedStorageMB / 1024) * 100 > 80 && (
                        <div className="alert alert-warning mb-0">
                          <i className="fas fa-exclamation-triangle me-2"></i>
                          <strong>Storage Warning:</strong> You are using
                          {(
                            (storageInfo.catalogUsedStorageMB / 1024) *
                            100
                          ).toFixed(2)}
                          % of your storage limit. Consider deleting unused
                          catalogs to free up space.
                        </div>
                      )}
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleUpload}>
                      <div className="row">
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="form-label fw-semibold">
                              Catalog File
                            </label>
                            <div className="input-group">
                              <input
                                type="file"
                                className="form-control"
                                id="catalog-file"
                                accept=".pdf"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    if (file.type !== "application/pdf") {
                                      toast.error("Please select a PDF file");
                                      e.target.value = "";
                                      setSelectedFile(null);
                                      return;
                                    }
                                    setSelectedFile(file);
                                  }
                                }}
                                required
                              />
                            </div>
                            <small className="form-text text-muted">
                              Supported formats: PDF
                            </small>
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="form-group mb-3">
                            <label className="form-label fw-semibold">
                              Display Name
                            </label>
                            <input
                              type="text"
                              name="display_name"
                              placeholder="Enter catalog display name"
                              value={display_name}
                              onChange={(e) => setDisplayName(e.target.value)}
                              className="form-control"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-md-12">
                          {uploading && (
                            <div className="form-group mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="text-muted">Upload Progress</small>
                                <small className="text-muted">{Math.round(uploadProgress)}%</small>
                              </div>
                              <div className="progress" style={{ height: "8px" }}>
                                <div
                                  className="progress-bar progress-bar-striped progress-bar-animated"
                                  role="progressbar"
                                  style={{ width: `${uploadProgress}%` }}
                                  aria-valuenow={uploadProgress}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                ></div>
                              </div>
                            </div>
                          )}
                          <div className="form-group">
                            <button
                              type="submit"
                              className="btn btn-primary w-100"
                              disabled={uploading || !selectedFile}
                            >
                              {uploading ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Uploading...
                                </>
                              ) : (
                                "Upload Catalog"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerCatalogs;
